/**
 * MirrorPlaneHelper - Creates and manages mirror plane visualization
 */
import * as THREE from 'three';

export class MirrorPlaneHelper {
  constructor(scene) {
    this.scene = scene;
    this.planeMesh = null;
  }

  /**
   * Show a semi-transparent mirror plane for the mirror tool
   * @param {Object} feature - Feature to show the plane at
   * @param {string} plane - Plane type ('xy', 'xz', or 'yz')
   */
  show(feature, plane) {
    // Remove any existing mirror plane
    this.hide();

    // Create plane geometry (2m x 2m)
    const planeSize = 2.0;
    const geometry = new THREE.PlaneGeometry(planeSize, planeSize);

    // Create semi-transparent material
    const material = new THREE.MeshBasicMaterial({
      color: 0x00ffff, // Cyan color
      transparent: true,
      opacity: 0.3,
      side: THREE.DoubleSide,
      depthWrite: false // Prevent z-fighting
    });

    const planeMesh = new THREE.Mesh(geometry, material);

    // Position and orient the plane based on feature and plane type
    // Convert feature position to Three.js coordinates
    const position = new THREE.Vector3(
      feature.position.x,
      feature.position.z,
      -feature.position.y
    );

    // Apply feature rotation if it exists
    if (feature.rotation) {
      const { rx, ry, rz } = feature.rotation;
      const angle = Math.sqrt(rx * rx + ry * ry + rz * rz);

      if (angle > 1e-10) {
        // Apply feature rotation using axis-angle
        const axis = new THREE.Vector3(rx, rz, -ry).normalize();
        planeMesh.rotateOnAxis(axis, angle);
      }
    }

    // Orient plane based on type (in feature's local coordinate system)
    // Note: Three.js plane geometry is in XY plane by default
    // Robot coords: X → Three.js X, Y → Three.js -Z, Z → Three.js Y
    switch (plane) {
      case 'xy':
        // XY plane in robot coords = X(-Z) plane in Three.js
        planeMesh.rotateX(Math.PI / 2);
        break;
      case 'xz':
        // XZ plane in robot coords = XY plane in Three.js (robot Z = Three.js Y)
        // No rotation needed
        break;
      case 'yz':
        // YZ plane in robot coords = (-Z)Y plane in Three.js
        planeMesh.rotateY(Math.PI / 2);
        break;
    }

    planeMesh.position.copy(position);

    // Store reference and add to scene
    this.planeMesh = planeMesh;
    this.scene.add(planeMesh);
  }

  /**
   * Hide the mirror plane
   */
  hide() {
    if (this.planeMesh) {
      this.scene.remove(this.planeMesh);
      this.planeMesh.geometry.dispose();
      this.planeMesh.material.dispose();
      this.planeMesh = null;
    }
  }

  /**
   * Clean up resources
   */
  dispose() {
    this.hide();
  }
}
